import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildOaiqUser } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { OaiqPixelAdapter, OAIQ_ADAPTER_KEY, OAIQ_SCRIPT };

const OAIQ_ADAPTER_KEY = "oaiq";
const OAIQ_SCRIPT = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

/**
 * Adaptador del píxel de ChatGPT Ads (`oaiq`).
 *
 * Particularidades de la red:
 *
 * - **Cargar el SDK una sola vez** (doc oficial): si `window.oaiq` ya existe
 *   no se redefine.
 * - **`measureSingle` siempre**: `measure` hace broadcast a todos los píxeles
 *   inicializados en la página, también a los ajenos.
 * - **Re-init con usuario SÍ está documentado**: "if user data becomes
 *   available after the first init, call init again with the complete user
 *   object". Por eso, a diferencia de Meta, aquí el init se repite cuando el
 *   optin aporta identidad.
 * - **Advanced matching pre-hasheado**: los hashes se calculan con
 *   `crypto.subtle` (asíncrono), así que el init "vacío" se hace YA — un
 *   píxel inicializado tarde no recibe los eventos anteriores — y el init con
 *   usuario llega detrás cuando el hash resuelve.
 * - **Custom events**: nombre `'custom'` y el real en `custom_event_name`.
 * - **`event_id`** (snake_case, no `eventID`) es el gancho de la dedup con la
 *   Conversions API: gana el primero que llegue.
 */
class OaiqPixelAdapter implements BrowserPixelAdapter {
  readonly key = OAIQ_ADAPTER_KEY;
  private _loaded = false;
  private _initialized: Record<string, boolean> = {};
  // Huella de la última identidad aplicada por píxel: evita re-inits en bucle
  // si el mismo optin dispara varios eventos.
  private _identityApplied: Record<string, string> = {};
  private _onScriptError: ScriptErrorReporter | null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      if (window.oaiq) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const oaiq: any = function () {
        // eslint-disable-next-line prefer-rest-params
        oaiq.queue.push(arguments);
      };
      oaiq.queue = [];
      // Seguro sobre el nombre de la cola. El stub de Meta reproduce su
      // snippet oficial, que está publicado; el de OpenAI está escrito a mano
      // porque no lo está, y TODO lo que se manda antes de que cargue
      // `oaiq.min.js` (el `init` y el `page_view`) pasa obligatoriamente por la
      // cola: si su SDK drenase `.q` —el otro patrón habitual— en lugar de
      // `.queue`, la red no recibiría ni un evento y el fallo sería invisible
      // (`pixel_script_error` solo salta si el script no carga). Los dos
      // nombres apuntan al MISMO array, así que da igual cuál drene.
      // TODO: verificar contra un píxel real en Events Manager y quedarse con
      // el nombre bueno.
      oaiq.q = oaiq.queue;
      window.oaiq = oaiq;

      injectVendorScript(OAIQ_SCRIPT, this.key, this._onScriptError);
    } catch (e) {
      // sin píxel de OpenAI la página sigue funcionando igual
    }
  }

  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!pixelId) {
        return;
      }
      const oaiq = this._oaiq();
      if (!oaiq) {
        return;
      }

      if (!this._initialized[pixelId]) {
        this._initialized[pixelId] = true;
        oaiq("init", { pixelId });
      }

      if (!identity) {
        return;
      }
      const fingerprint = JSON.stringify([
        identity.email ?? "",
        identity.phone ?? "",
        identity.firstName ?? "",
        identity.lastName ?? "",
        identity.visitorId ?? "",
      ]);
      if (this._identityApplied[pixelId] === fingerprint) {
        return;
      }
      this._identityApplied[pixelId] = fingerprint;

      void buildOaiqUser(identity)
        .then((user) => {
          try {
            if (Object.keys(user).length === 0) {
              return;
            }
            this._oaiq()?.("init", { pixelId, user });
          } catch (e) {
            // el re-init con usuario es una mejora de matching, no un
            // requisito: si falla, los eventos siguen saliendo
          }
        })
        .catch(() => {
          // sin contexto seguro no hay hash: evento sin datos de usuario
        });
    } catch (e) {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      const oaiq = this._oaiq();
      if (!oaiq || !pixelId || !event?.name) {
        return;
      }
      const params = event.params ?? {};
      if (event.custom) {
        oaiq("measureSingle", pixelId, "custom", params, {
          event_id: eventId,
          custom_event_name: event.name,
        });
        return;
      }
      oaiq("measureSingle", pixelId, event.name, params, {
        event_id: eventId,
      });
    } catch (e) {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      this._oaiq()?.("consent", granted);
    } catch (e) {
      // sin consentimiento aplicado, el píxel se queda como estaba
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _oaiq(): any {
    return typeof window.oaiq === "function" ? window.oaiq : null;
  }
}
